import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dagnai9tto0ceqqni0s0";

type Status = "idle" | "success" | "error";

const perks = [
  { icon: "ri-mail-volume-line", text: "每日 8 点准时送达" },
  { icon: "ri-sparkling-line", text: "本周精选一次收齐" },
  { icon: "ri-notification-3-line", text: "大瓜速报不错过" },
  { icon: "ri-price-tag-3-line", text: "永久免费，随时退订" },
];

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐：命中则静默当作成功处理
    const honeypot = fd.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      setMessage("订阅成功，欢迎加入瓜田！");
      form.reset();
      return;
    }

    fd.delete("website_alt");

    setSubmitting(true);
    setStatus("idle");
    setMessage("");
    try {
      const body = new URLSearchParams();
      fd.forEach((value, key) => {
        body.append(key, String(value));
      });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed?.meta?.message) ||
        (parsed?.message) ||
        (parsed?.meta?.detail) ||
        responseText ||
        "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage("订阅成功，欢迎加入瓜田，明早 8 点见！");
        form.reset();
      } else {
        const isSpam = /spam/i.test(serverMsg) || /form data is spam/i.test(serverMsg);
        setStatus("error");
        setMessage(isSpam ? "提交过于频繁，请稍后再试。" : serverMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请检查后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="subscribe" className="relative overflow-hidden py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-primary-500 px-6 py-10 md:px-12 md:py-14">
          <div
            className="pointer-events-none absolute inset-0 opacity-90"
            style={{
              background:
                "radial-gradient(circle at 15% 20%, rgb(255 255 255/0.14), transparent 38%), radial-gradient(circle at 88% 80%, rgb(36 36 36/0.10), transparent 42%)",
            }}
            aria-hidden="true"
          ></div>
          <div className="relative grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/20 px-3.5 py-1.5 text-xs font-medium text-background-50">
                <i className="ri-notification-3-line" aria-hidden="true"></i>
                免费 · 可随时退订
              </span>
              <h2 className="font-heading mt-4 text-2xl font-bold text-background-50 md:text-3xl">
                订阅瓜料日报，热门瓜一个不落
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-background-50/90 md:text-base">
                留下邮箱，每天把最值得吃的瓜和一周精选直接送到你手里，不用再自己来回翻找。
              </p>
              <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
                {perks.map((p) => (
                  <li key={p.text} className="flex items-center gap-1.5 text-xs text-background-50/90">
                    <i className={`${p.icon} text-sm text-background-50`} aria-hidden="true"></i>
                    {p.text}
                  </li>
                ))}
              </ul>
            </div>

            <form
              id="subscribe-form"
              data-readdy-form="daily-melon-newsletter"
              onSubmit={handleSubmit}
              className="rounded-2xl bg-background-50 p-5 md:p-6"
              noValidate
            >
              <label htmlFor="newsletter-email" className="block text-sm font-semibold text-foreground-900">
                你的订阅邮箱
              </label>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@example.com"
                  className="min-w-0 flex-1 rounded-full border border-background-300 bg-background-100 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line mr-1.5 animate-spin" aria-hidden="true"></i>
                      提交中
                    </>
                  ) : (
                    <>
                      立即订阅
                      <i className="ri-arrow-right-line ml-1.5" aria-hidden="true"></i>
                    </>
                  )}
                </button>
              </div>
              {/* 反垃圾蜜罐字段 */}
              <input
                type="text"
                name="website_alt"
                className="hp-guard-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              {status === "success" && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-accent-700">
                  <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === "error" && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-primary-700" role="alert">
                  <i className="ri-error-warning-line" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              <p className="mt-3 text-xs text-foreground-500">
                提交即代表同意接收每日瓜料日报，我们不会把邮箱用于任何其它用途。
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}